import { useCallback, useMemo, useState } from 'react';
import { reorderColumn } from './column-preference';

export type ColumnDropTarget = { readonly beforeId: string | null };

export type ColumnDrag = {
  readonly draggedId: string | null;
  readonly target: ColumnDropTarget | null;
  readonly start: (id: string) => void;
  readonly hover: (target: ColumnDropTarget) => void;
  readonly clear: () => void;
  readonly drop: () => void;
};

/**
 * One hook for both surfaces, because both write the same order and both need the
 * same three pieces of state. Every returned member is stabilised — a hook whose
 * return value is an object is dependency-array input for its consumer (BL-035).
 */
export function useColumnDrag(
  order: ReadonlyArray<string>,
  onReorder: (next: ReadonlyArray<string>) => void,
): ColumnDrag {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [target, setTarget] = useState<ColumnDropTarget | null>(null);

  const start = useCallback((id: string) => {
    setDraggedId(id);
    setTarget(null);
  }, []);

  const hover = useCallback((next: ColumnDropTarget) => {
    // `dragover` fires continuously while the pointer sits still, so re-setting
    // an unchanged target would re-render the whole table several times a second
    // for the length of the drag. Returning `current` makes React bail out.
    setTarget((current) => (current !== null && current.beforeId === next.beforeId ? current : next));
  }, []);

  const clear = useCallback(() => {
    setDraggedId(null);
    setTarget(null);
  }, []);

  const drop = useCallback(() => {
    if (draggedId !== null && target !== null) {
      const next = reorderColumn(order, draggedId, target.beforeId);

      // The SAME reference means the drop changed nothing. Writing anyway would
      // let a member who dropped a column back where it started get „Spalten
      // konnten nicht gespeichert werden" for a change they did not make.
      if (next !== order) onReorder(next);
    }

    setDraggedId(null);
    setTarget(null);
  }, [draggedId, target, order, onReorder]);

  return useMemo(
    () => ({ draggedId, target, start, hover, clear, drop }),
    [draggedId, target, start, hover, clear, drop],
  );
}
